import { format, formatDistanceToNowStrict } from 'date-fns';
import { getApiErrorMessage } from '@/lib/utils';
import type { FollowupRow } from './types';

/** "in 3 hours" / "5 minutes ago", and the exact instant for the tooltip and screen readers. */
export function followupWhen(iso: string, now: Date = new Date()): { relative: string; absolute: string } | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const relative = formatDistanceToNowStrict(date, { addSuffix: true });
  return { relative: Math.abs(date.getTime() - now.getTime()) < 60_000 ? 'now' : relative, absolute: format(date, 'd MMM yyyy, HH:mm') };
}

/** Event text from the server names times in UTC ISO ("Waiting until 2026-10-15T13:29:13.597Z"); show them in local time. */
export const followupEventText = (text: string): string =>
  text.replace(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z/g, (iso) => format(new Date(iso), 'd MMM yyyy, HH:mm'));

/** Where "Open workflow" goes: the protocol editor (every workflow opens as a protocol), at the step the wait came from. */
export function followupWorkflowHref(workflow: FollowupRow['workflow']): string | null {
  if (!workflow.id) return null;
  const base = `/training/protocols/${encodeURIComponent(workflow.id)}`;
  return workflow.nodeId ? `${base}?node=${encodeURIComponent(workflow.nodeId)}` : base;
}

/** 410 means the wait ended under the Sentinel; every other refusal carries its own message (a 409 names the hold). */
export function followupActionError(error: unknown, fallback: string): string {
  const status = (error as { response?: { status?: number } } | null)?.response?.status;
  if (status === 410) return 'This follow-up has already ended.';
  return getApiErrorMessage(error, fallback);
}

/** Whether the Sentinel can send this follow-up's draft: a live, queued draft, blocked or not. */
export const canSendFollowup = (row: FollowupRow, canEdit: boolean) =>
  canEdit && row.state === 'queued' && row.draft !== null && row.mode === 'live';
